/**
 * ProductTourDataService
 * 
 * Tour product verilerini parse eden ve transform eden utility class
 * OOP prensiplerine uygun, static method'lar ile merkezi yapı
 */

import { transformMediaUrl } from '@/lib/media-url-transformer';
import {
    CANCELLATION_TERMS_FIELD_KEY,
    CANCELLATION_TERMS_SECTION_ID,
    hasMeaningfulHtml,
    OTHER_DESCRIPTIONS_GROUP_KEY,
    slugifySectionId,
} from '@/utils/productOtherDescriptionsUtils';

export interface TourDuration {
    day: number;
    night: number;
}

export interface TourDayHotel {
    hotel_id: number;
    hotel_name?: string | null;
    nights?: number | null;
    meals?: string[] | null;
    meals_custom?: string | null;
}
export interface TourStop {
    location_title?: string;
    location_address?: string;
    lat?: number;
    lng?: number;
    duration?: string;
    note?: string;
    /** Durak detay açıklaması (admin) */
    description?: string;
    location_description?: string;
    location_type?: string;
}

export interface TourDay {
    id: string;
    dayNumber: number;
    title: string;
    description: string;
    images: string[];
    /** Lightbox'ta açılacak tam boyut URL'leri (API url_full; yoksa images ile aynı) */
    imagesFull?: string[];
    imageIds: number[];
    hotels?: TourDayHotel[];
    // Legacy support (deprecated)
    hotel?: number | number[] | null;
    nights?: number | null;
    /** Öğün: API `meal` (metin/dizi), `meals` veya legacy dizi */
    meal?: string | string[] | null;
    /** Mesafe: sayı veya metin (örn. "320 km") */
    distance?: number | string | null;
    meals_custom?: string | null;
    location_type?: string;
    location_title?: string;
    location_description?: string;
    location_address?: string;
    lat?: number;
    lng?: number;
    /** Admin çoklu durak (tur programı editörü) */
    stops?: TourStop[];
    time_start?: string;
    time_end?: string;
    extra_info?: string;
    description_text?: string;
    description_html?: string;
}

export interface Service {
    title?: string;
    name?: string;
    value?: string;
    text?: string;
    description?: string;
    desc?: string;
    detail?: string;
}

export interface PricingRow {
    paxRange: string;
    perPersonDouble: number;
    singleRoom: number;
    extraBed: number;
    childrenFee: number;
}

export interface PricingPeriod {
    packageName: string;
    periodStart: string;
    periodEnd: string;
    rows: PricingRow[];
}

/** Sabit çıkış tarihi (grup turları) – admin takvimden seçilen günler */
export interface DepartureDateEntry {
    date: string; // YYYY-MM-DD
    status?: 'available' | 'discounted' | 'stop_sale';
}

export class ProductTourDataService {
    /**
     * Tour duration'ı parse et (meta'dan)
     */
    static parseTourDuration(meta: any): TourDuration | null {
        if (!meta?.tour_duration) return null;

        const duration = meta.tour_duration;
        
        // Eğer object ise direkt kullan
        if (typeof duration === 'object' && duration !== null) {
            const day = typeof duration.day === 'number' ? duration.day : 0;
            const night = typeof duration.night === 'number' ? duration.night : 0;
            return { day, night };
        }

        // Eğer string ise parse et (örn: "13 Days - 12 Nights")
        if (typeof duration === 'string') {
            const dayMatch = duration.match(/(\d+)\s*(?:day|gün)/i);
            const nightMatch = duration.match(/(\d+)\s*(?:night|gece)/i);
            const day = dayMatch ? parseInt(dayMatch[1]) : 0;
            const night = nightMatch ? parseInt(nightMatch[1]) : 0;
            return { day, night };
        }

        return null;
    }

    /**
     * Admin'de sadece medya ID kayıtlıysa (url yok) Next proxy üzerinden görüntüleme URL'si üretir.
     */
    private static tourDayMediaIdToProxyUrl(mediaId: number): string {
        const u = transformMediaUrl(`/storage/media/${mediaId}`, null);
        return u && u.trim() !== "" ? u : `/api/media/proxy/${mediaId}`;
    }

    /**
     * Aynı gün için API + DB birleşiminde aynı medya birden fazla kez (id + filename proxy vb.) gelebilir.
     * Tekilleştirme anahtarı: medya id (varsa), yoksa proxy path'teki sayısal id, yoksa URL (query'siz).
     */
    private static dedupeTourDayImageItems(
        items: { id?: number; url: string; full?: string }[],
    ): { images: string[]; imagesFull: string[]; imageIds: number[] } {
        const images: string[] = [];
        const imagesFull: string[] = [];
        const imageIds: number[] = [];
        const seenKeys = new Set<string>();

        const keyFor = (r: { id?: number; url: string }): string => {
            if (r.id != null && Number.isFinite(r.id)) {
                return `id:${r.id}`;
            }
            const nu = r.url.trim().split("?")[0];
            const m = nu.match(/\/api\/media\/proxy\/(\d+)(?:\/|$)/);
            if (m) {
                return `id:${parseInt(m[1], 10)}`;
            }
            return `u:${nu}`;
        };

        for (const r of items) {
            const url = (r.url || "").trim();
            if (!url) continue;
            const k = keyFor({ id: r.id, url });
            if (seenKeys.has(k)) continue;
            seenKeys.add(k);

            const full = (r.full && r.full.trim()) || url;
            images.push(url);
            imagesFull.push(full);

            let mid: number | undefined =
                r.id != null && Number.isFinite(r.id) ? r.id : undefined;
            if (mid === undefined) {
                const nu = url.split("?")[0];
                const m = nu.match(/\/api\/media\/proxy\/(\d+)(?:\/|$)/);
                if (m) {
                    mid = parseInt(m[1], 10);
                }
            }
            if (mid !== undefined) {
                imageIds.push(mid);
            }
        }

        return { images, imagesFull, imageIds };
    }

    /**
     * Tour program'ı parse et (meta.tour_program: API'dan dizi veya JSON string gelebilir)
     */
    static parseTourProgram(meta: any): TourDay[] {
        let program = meta?.tour_program;
        if (program == null) return [];
        if (typeof program === 'string' && program.trim()) {
            try {
                const parsed = JSON.parse(program);
                program = Array.isArray(parsed) ? parsed : null;
            } catch {
                return [];
            }
        }
        if (!Array.isArray(program)) return [];

        return program.map((day: any, index: number) => {
            const rawItems: { id?: number; url: string; full?: string }[] = [];
            // Admin paneli bazen images'ı JSON string olarak kaydediyor; string ise parse et
            let dayImages = day.images;
            if (typeof dayImages === 'string' && dayImages.trim()) {
                try {
                    const parsed = JSON.parse(dayImages);
                    dayImages = Array.isArray(parsed) ? parsed : dayImages;
                } catch {
                    dayImages = [];
                }
            }
            if (Array.isArray(dayImages) && dayImages.length > 0) {
                for (const item of dayImages) {
                    if (typeof item === 'number') {
                        const id = item;
                        if (!Number.isFinite(id)) continue;
                        const u = ProductTourDataService.tourDayMediaIdToProxyUrl(id);
                        rawItems.push({ id, url: u, full: u });
                    } else if (typeof item === 'string' && item.trim()) {
                        const url = transformMediaUrl(item);
                        if (url) rawItems.push({ url });
                    } else if (item && typeof item === 'object') {
                        // API'den gelen url (medya proxy) varsa önce onu kullan
                        if (item.url || item.path) {
                            const url = transformMediaUrl(item.url || item.path);
                            if (!url) continue;
                            const fullRaw = item.url_full ? transformMediaUrl(item.url_full) : url;
                            const rawId = item.media_id ?? item.id;
                            let numId: number | undefined = undefined;
                            if (typeof rawId === 'number' && Number.isFinite(rawId)) {
                                numId = rawId;
                            } else if (typeof rawId === 'string' && /^\d+$/.test(rawId.trim())) {
                                numId = parseInt(rawId.trim(), 10);
                            }
                            rawItems.push({
                                id: numId,
                                url,
                                full: fullRaw || url,
                            });
                        } else {
                            const rawId = item.media_id ?? item.id;
                            let numId: number | null = null;
                            if (typeof rawId === 'number' && Number.isFinite(rawId)) {
                                numId = rawId;
                            } else if (typeof rawId === 'string' && /^\d+$/.test(rawId.trim())) {
                                numId = parseInt(rawId.trim(), 10);
                            }
                            if (numId != null) {
                                const u = ProductTourDataService.tourDayMediaIdToProxyUrl(numId);
                                rawItems.push({ id: numId, url: u, full: u });
                            }
                        }
                    }
                }
            }
            const { images, imagesFull, imageIds } =
                ProductTourDataService.dedupeTourDayImageItems(rawItems);
            return ProductTourDataService.buildTourDay(
                day,
                index,
                images,
                imageIds,
                imagesFull.length > 0 ? imagesFull : undefined,
            );
        });
    }

    /**
     * Parse hotels array from day data
     */
    private static parseHotels(day: any): TourDayHotel[] | undefined {
        // Önce hotels key'ini kontrol et (backend API'den gelen format - hotels_widget'ten dönüştürülmüş)
        // Sonra hotels_widget key'ini kontrol et (backward compatibility için)
        const hotelsSource = day.hotels || day.hotels_widget;
        
        if (!hotelsSource) {
            return undefined;
        }

        let hotelsData: any[] = [];
        
        // Parse hotels (can be array or JSON string)
        if (Array.isArray(hotelsSource)) {
            hotelsData = hotelsSource;
        } else if (typeof hotelsSource === 'string') {
            try {
                const parsed = JSON.parse(hotelsSource);
                if (Array.isArray(parsed)) {
                    hotelsData = parsed;
                }
            } catch {
                // Invalid JSON, skip
                return undefined;
            }
        } else {
            return undefined;
        }

        // Parse each hotel
        const hotels: TourDayHotel[] = [];
        for (const hotelData of hotelsData) {
            if (!hotelData || typeof hotelData !== 'object') {
                continue;
            }

            const hotelId = hotelData.hotel_id ?? hotelData.hotel;
            if (!hotelId || isNaN(Number(hotelId))) {
                continue;
            }

            // Parse nights
            const nights = hotelData.nights !== undefined && hotelData.nights !== null
                ? (typeof hotelData.nights === 'string' ? parseInt(hotelData.nights) : Number(hotelData.nights))
                : null;

            // Parse meals
            let meals: string[] | undefined = undefined;
            if (hotelData.meals) {
                if (Array.isArray(hotelData.meals)) {
                    meals = hotelData.meals.filter((m: any) => m && String(m).trim());
                } else if (typeof hotelData.meals === 'string') {
                    try {
                        const parsed = JSON.parse(hotelData.meals);
                        if (Array.isArray(parsed)) {
                            meals = parsed.filter((m: any) => m && String(m).trim());
                        }
                    } catch {
                        // Invalid JSON, skip meals
                    }
                }
            }

            // Parse meals_custom
            const meals_custom = hotelData.meals_custom && String(hotelData.meals_custom).trim()
                ? String(hotelData.meals_custom).trim()
                : undefined;

            // Parse hotel_name (backend'den gelen)
            const hotel_name = hotelData.hotel_name && typeof hotelData.hotel_name === 'string' && hotelData.hotel_name.trim()
                ? hotelData.hotel_name.trim()
                : undefined;

            hotels.push({
                hotel_id: Number(hotelId),
                hotel_name,
                nights: nights !== null && !isNaN(nights) ? nights : undefined,
                meals: meals && meals.length > 0 ? meals : undefined,
                meals_custom,
            });
        }

        return hotels.length > 0 ? hotels : undefined;
    }

    /** `meal` / `meals` / legacy gün öğün alanlarını tek alanda birleştirir */
    private static coalesceDayMealField(day: any, legacyMeals: string[] | null): string | string[] | null {
        const parseOne = (value: unknown): string | string[] | null => {
            if (value == null || value === "") return null;
            if (Array.isArray(value)) {
                const a = value.map((m) => String(m).trim()).filter(Boolean);
                return a.length ? a : null;
            }
            if (typeof value === "string") {
                const t = value.trim();
                if (!t) return null;
                if (t.startsWith("[")) {
                    try {
                        const p = JSON.parse(t) as unknown;
                        if (Array.isArray(p)) {
                            const a = p.map((m) => String(m).trim()).filter(Boolean);
                            return a.length ? a : null;
                        }
                    } catch {
                        /* metin */
                    }
                }
                return t;
            }
            return String(value).trim() || null;
        };

        return (
            parseOne(day.meal) ||
            parseOne(day.meals) ||
            (legacyMeals && legacyMeals.length ? legacyMeals : null)
        );
    }

    private static normalizeDayDistance(value: unknown): string | number | null {
        if (value == null || value === "") return null;
        if (typeof value === "number") {
            return Number.isNaN(value) ? null : value;
        }
        const s = String(value).trim();
        return s === "" ? null : s;
    }

    /**
     * Tour day objesi oluştur
     */
    private static buildTourDay(day: any, index: number, images: string[], imageIds: number[], imagesFull?: string[]): TourDay {
        let lat: number | undefined = undefined;
        let lng: number | undefined = undefined;

        const stopsRaw = Array.isArray(day.stops) ? day.stops : [];
        const stopsParsed: TourStop[] = [];
        for (const s of stopsRaw) {
            if (!s || typeof s !== 'object') continue;
            let slat: number | undefined;
            let slng: number | undefined;
            if (s.lat !== undefined && s.lat !== null && s.lat !== '') {
                const parsedLat = typeof s.lat === 'string' ? parseFloat(s.lat) : Number(s.lat);
                if (!isNaN(parsedLat) && isFinite(parsedLat)) slat = parsedLat;
            }
            if (s.lng !== undefined && s.lng !== null && s.lng !== '') {
                const parsedLng = typeof s.lng === 'string' ? parseFloat(s.lng) : Number(s.lng);
                if (!isNaN(parsedLng) && isFinite(parsedLng)) slng = parsedLng;
            }
            stopsParsed.push({
                location_title: s.location_title ?? s.title,
                location_address: s.location_address ?? s.address,
                lat: slat,
                lng: slng,
                duration: s.duration,
                note: s.note,
                description: s.description ?? s.location_description,
                location_description: s.location_description,
                location_type: s.location_type ?? s.type,
            });
            if (lat === undefined && slat !== undefined && slng !== undefined) {
                lat = slat;
                lng = slng;
            }
        }

        if (day.lat !== undefined && day.lat !== null && day.lat !== '') {
            const parsedLat = typeof day.lat === 'string' ? parseFloat(day.lat) : Number(day.lat);
            if (!isNaN(parsedLat) && isFinite(parsedLat)) {
                lat = lat ?? parsedLat;
            }
        }

        if (day.lng !== undefined && day.lng !== null && day.lng !== '') {
            const parsedLng = typeof day.lng === 'string' ? parseFloat(day.lng) : Number(day.lng);
            if (!isNaN(parsedLng) && isFinite(parsedLng)) {
                lng = lng ?? parsedLng;
            }
        }
        
        // Parse hotels (new structure)
        const hotels = ProductTourDataService.parseHotels(day);

        // Legacy support: Parse old structure (hotel, nights, meals, meals_custom at day level)
        let hotel: number | number[] | null = null;
        let nights: number | null = null;
        let meals: string[] | null = null;
        let meals_custom: string | null = null;

        // Only use legacy if hotels array is not present
        if (!hotels) {
            hotel = day.hotel || null;
            
            // Parse meals (can be array or string)
            if (day.meals) {
                if (Array.isArray(day.meals)) {
                    meals = day.meals.filter((m: any) => m && String(m).trim());
                } else if (typeof day.meals === 'string') {
                    try {
                        const parsed = JSON.parse(day.meals);
                        meals = Array.isArray(parsed) ? parsed.filter((m: any) => m && String(m).trim()) : [day.meals];
                    } catch {
                        meals = [day.meals];
                    }
                }
            }

            // Parse nights
            nights = day.nights !== undefined && day.nights !== null 
                ? (typeof day.nights === 'string' ? parseInt(day.nights) : Number(day.nights))
                : null;

            // Parse meals_custom
            meals_custom = day.meals_custom && String(day.meals_custom).trim()
                ? String(day.meals_custom).trim()
                : null;
        }

        const mealsCustomUnified =
            day.meals_custom && String(day.meals_custom).trim()
                ? String(day.meals_custom).trim()
                : meals_custom;

        const legacyDesc = typeof day.description === 'string' ? day.description : '';
        const htmlPart =
            day.description_html != null && String(day.description_html).trim() !== ''
                ? String(day.description_html)
                : '';
        const textPart =
            day.description_text != null && String(day.description_text).trim() !== ''
                ? String(day.description_text)
                : '';
        let mergedDescription = '';
        if (htmlPart) {
            mergedDescription = htmlPart;
        } else if (textPart) {
            mergedDescription = textPart.replace(/\n/g, '<br/>');
        } else {
            mergedDescription = legacyDesc;
        }

        return {
            id: `day-${day.day || index + 1}`,
            dayNumber: day.day || index + 1,
            title: day.title || `Day ${day.day || index + 1}`,
            description: mergedDescription,
            images,
            imagesFull,
            imageIds,
            hotels,
            // Legacy support
            hotel: hotel || null,
            nights: nights !== null && !isNaN(nights) ? nights : null,
            meal: ProductTourDataService.coalesceDayMealField(day, meals),
            distance: ProductTourDataService.normalizeDayDistance(day.distance),
            meals_custom: mealsCustomUnified,
            location_type: day.location_type,
            location_title: day.location_title,
            location_description: day.location_description,
            location_address: day.location_address,
            lat,
            lng,
            stops: stopsParsed.length > 0 ? stopsParsed : undefined,
            time_start: day.time_start,
            time_end: day.time_end,
            extra_info: day.extra_info,
            description_text: day.description_text,
            description_html: day.description_html,
        };
    }

    /**
     * Tour pricing'ı parse et
     * Backend'den product.tour_pricing veya meta.tour_pricing'den çeker
     */
    static parseTourPricing(product: any, meta: any): PricingPeriod[] {
        // Önce product.tour_pricing'i kontrol et (backend'den direkt geliyor)
        let pricingData = product?.tour_pricing || null;
        
    
        // Eğer product.tour_pricing yoksa meta'dan çek (eski sistem için fallback)
        if (!pricingData || !Array.isArray(pricingData)) {
            pricingData = meta?.tour_pricing || meta?.price_details || meta?.pricing || null;
        }
        
        if (!pricingData || !Array.isArray(pricingData)) {
     
            return [];
        }

        return pricingData.map((period: any) => ({
            packageName: period.packageName || period.package_name || period.name || period.package || 'STANDARD',
            periodStart: period.periodStart || period.period_start || period.start_date || period.start || '',
            periodEnd: period.periodEnd || period.period_end || period.end_date || period.end || '',
            rows: Array.isArray(period.rows) ? period.rows.map((row: any) => ({
                paxRange: row.paxRange || row.pax_range || row.pax || row.range || '',
                perPersonDouble: row.perPersonDouble !== undefined && row.perPersonDouble !== null 
                    ? parseFloat(row.perPersonDouble) 
                    : parseFloat(row.per_person_double || row.double || row.per_person || 0),
                singleRoom: row.singleRoom !== undefined && row.singleRoom !== null
                    ? parseFloat(row.singleRoom)
                    : parseFloat(row.single_room || row.single || row.single_room_price || 0),
                extraBed: row.extraBed !== undefined && row.extraBed !== null
                    ? parseFloat(row.extraBed)
                    : parseFloat(row.extra_bed || row.extra || row.extra_bed_price || 0),
                childrenFee: row.childrenFee !== undefined && row.childrenFee !== null
                    ? parseFloat(row.childrenFee)
                    : parseFloat(row.children_fee || row.children || row.child_price || 0),
            })) : [],
        }));
    }

    /**
     * Sabit çıkış tarihlerini parse et (metas.departure_dates veya fields.tour_departure_dates)
     * Format: [{ date: "YYYY-MM-DD", status?: "available"|"discounted"|"stop_sale" }, ...]
     */
    static parseDepartureDates(meta: any, fields?: any): DepartureDateEntry[] {
        let raw = meta?.departure_dates ?? fields?.tour_departure_dates ?? null;
        if (typeof raw === 'string' && raw.trim()) {
            try {
                raw = JSON.parse(raw);
            } catch {
                return [];
            }
        }
        if (!Array.isArray(raw) || raw.length === 0) return [];
        const result: DepartureDateEntry[] = [];
        for (const item of raw) {
            const date = item?.date ?? item?.departure_date ?? null;
            if (!date || typeof date !== 'string') continue;
            const normalized = date.trim().slice(0, 10);
            if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) continue;
            const status = item?.status;
            const validStatus = status === 'available' || status === 'discounted' || status === 'stop_sale' ? status : undefined;
            result.push({ date: normalized, status: validStatus });
        }
        result.sort((a, b) => a.date.localeCompare(b.date));
        return result;
    }

    /**
     * Services'ı parse et (included/excluded)
     */
    static parseServices(meta: any): { included: Service[]; excluded: Service[] } {
        const included: Service[] = [];
        const excluded: Service[] = [];

        // Helper function to parse and normalize services data
        const parseServicesData = (data: any): any[] => {
            if (!data) return [];
            
            // If it's a JSON string, parse it
            if (typeof data === 'string') {
                try {
                    const parsed = JSON.parse(data);
                    return Array.isArray(parsed) ? parsed : [];
                } catch {
                    // If parsing fails, treat as single string
                    return [data];
                }
            }
            
            // If it's already an array, return it
            if (Array.isArray(data)) {
                return data;
            }
            
            // If it's an object, wrap it in an array
            if (typeof data === 'object' && data !== null) {
                return [data];
            }
            
            return [];
        };

        // Helper function to extract service data from various formats
        const extractService = (service: any): Service | null => {
            // Null veya undefined kontrolü
            if (service === null || service === undefined) {
                return null;
            }
            
            // String ise direkt kullan
            if (typeof service === 'string') {
                const trimmed = service.trim();
                return trimmed ? { title: trimmed, description: '' } : null;
            }
            
            // Object ise title'ı çıkar
            if (typeof service === 'object' && service !== null) {
                // Try multiple possible field names
                let title = '';
                if (service.title !== undefined && service.title !== null) {
                    title = String(service.title);
                } else if (service.name !== undefined && service.name !== null) {
                    title = String(service.name);
                } else if (service.value !== undefined && service.value !== null) {
                    title = String(service.value);
                } else if (service.text !== undefined && service.text !== null) {
                    title = String(service.text);
                } else if (service.label !== undefined && service.label !== null) {
                    title = String(service.label);
                } else if (service.service !== undefined && service.service !== null) {
                    title = String(service.service);
                }
                
                const description = service.description !== undefined && service.description !== null
                    ? String(service.description)
                    : (service.desc !== undefined && service.desc !== null
                        ? String(service.desc)
                        : (service.detail !== undefined && service.detail !== null
                            ? String(service.detail)
                            : ''));
                
                // Only add if we have a title
                const trimmedTitle = title.trim();
                if (trimmedTitle) {
                    return { 
                        title: trimmedTitle, 
                        description: description.trim() 
                    };
                }
            }
            
            return null;
        };

        // Parse and process included services
        const includedData = parseServicesData(meta?.included_services);
        includedData.forEach((service: any) => {
            const extracted = extractService(service);
            if (extracted) {
                included.push(extracted);
                }
            });

        // Parse and process excluded services
        const excludedData = parseServicesData(meta?.excluded_services);
        excludedData.forEach((service: any) => {
            const extracted = extractService(service);
            if (extracted) {
                excluded.push(extracted);
                }
            });

        return { included, excluded };
    }

    /**
     * Transfer tiplerini parse et
     */
    static parseTransfer(meta: any): string[] {
        if (!meta?.transfer) return [];
        
        if (Array.isArray(meta.transfer)) {
            return meta.transfer.filter((t: any) => typeof t === 'string');
        }
        
        if (typeof meta.transfer === 'string') {
            try {
                const parsed = JSON.parse(meta.transfer);
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                return [meta.transfer];
            }
        }

        return [];
    }

    /**
     * Tour program'dan başlangıç/bitiş lokasyonlarını çıkar
     */
    static extractLocations(tourProgram: TourDay[]): { start: string | null; end: string | null } {
        if (!tourProgram || tourProgram.length === 0) {
            return { start: null, end: null };
        }

        const firstDay = tourProgram[0];
        const lastDay = tourProgram[tourProgram.length - 1];

        return {
            start: firstDay.location_title || firstDay.title || null,
            end: lastDay.location_title || lastDay.title || null,
        };
    }

    /**
     * Information field'ını parse et (rich_text)
     */
    static parseInformation(meta: any, fields: any, language: string = 'en'): { content: string | null; fieldTitle: string | null } {
        if (!meta || !fields) {
            return { content: null, fieldTitle: null };
        }

        const field = fields['information'];
        const value = meta['information'];

        if (!field || !field.has_data || !value || (typeof value === 'string' && value.trim() === '')) {
            return { content: null, fieldTitle: null };
        }

        // Field title'ı dil bazlı al
        let fieldTitle = 'Information';
        if (field.field_titles) {
            const titles = typeof field.field_titles === 'string' 
                ? JSON.parse(field.field_titles) 
                : field.field_titles;
            fieldTitle = titles[language] || titles['en'] || titles['tr'] || 'Information';
        }

        return {
            content: typeof value === 'string' ? value : String(value),
            fieldTitle,
        };
    }

    /**
     * Tab names ile gruplanmış rich_text field'ları parse et
     * Her tab için bir section oluşturur (scroll olarak alt alta)
     */
    static parseTabFields(meta: any, fields: any, language: string = 'en'): Array<{
        tabName: string;
        sectionId: string;
        showSectionTitle?: boolean;
        /** "Diğer Açıklamalar" grubu için (menüde dropdown gösterilir) */
        groupKey?: string;
        fields: Array<{
            fieldKey: string;
            fieldTitle: string;
            content: string;
        }>;
    }> {
        if (!meta || !fields) {
            return [];
        }

        const result: Array<{
            tabName: string;
            sectionId: string;
            showSectionTitle?: boolean;
            groupKey?: string;
            fields: Array<{
                fieldKey: string;
                fieldTitle: string;
                content: string;
            }>;
        }> = [];

        // 0. cancellation_terms – doluysa ayrı bölüm (HTML)
        const cancellationValue = meta[CANCELLATION_TERMS_FIELD_KEY];
        if (hasMeaningfulHtml(cancellationValue)) {
            const cancellationField = fields[CANCELLATION_TERMS_FIELD_KEY];
            const fieldTitle = cancellationField?.field_title || 'Cancellation Terms';
            result.push({
                tabName: fieldTitle,
                sectionId: CANCELLATION_TERMS_SECTION_ID,
                showSectionTitle: true,
                groupKey: OTHER_DESCRIPTIONS_GROUP_KEY,
                fields: [{
                    fieldKey: CANCELLATION_TERMS_FIELD_KEY,
                    fieldTitle,
                    content: typeof cancellationValue === 'string' ? cancellationValue : String(cancellationValue),
                }],
            });
        }

        // 1. tour_sections repeater field'ını parse et (özel durum)
        const tourSections = meta['tour_sections'];
        if (tourSections && Array.isArray(tourSections) && tourSections.length > 0) {
            const tabField = fields['tour_sections'];
            const baseTabName = tabField?.tab_name || 'Oteller'; // Default tab name
            
            tourSections.forEach((tabItem: any, index: number) => {
                if (!ProductTourDataService.isTruthyFlag(tabItem?.show_on_page)) {
                    return;
                }
                const title = tabItem.title || tabItem.name || `Section ${index + 1}`;
                const content = tabItem.content || tabItem.description || '';
                
                if (!content || (typeof content === 'string' && content.trim() === '')) {
                    return; // Boş içerik varsa atla
                }

                const sectionId = slugifySectionId(title, `tour-section-${index}`);

                const tourSectionsField = fields['tour_sections'];
                const showSectionTitle = tourSectionsField?.show_section_title !== false;
                result.push({
                    tabName: title,
                    sectionId,
                    showSectionTitle,
                    groupKey: OTHER_DESCRIPTIONS_GROUP_KEY,
                    fields: [{
                        fieldKey: 'tour_sections',
                        fieldTitle: title,
                        content: typeof content === 'string' ? content : String(content),
                    }],
                });
            });
        }

        // 2. Rich text field'ları tab_group_key ile grupla (yeni yapı)
        const tabGroups: Record<string, Array<{
            fieldKey: string;
            fieldTitle: string;
            content: string;
            tabDisplayOrder: number;
        }>> = {};

        // Tüm field'ları dolaş
        Object.entries(fields).forEach(([fieldKey, field]: [string, any]) => {
            // tour_sections ve cancellation_terms ayrı işlendi
            if (fieldKey === 'tour_sections' || fieldKey === CANCELLATION_TERMS_FIELD_KEY) {
                return;
            }

            // included_services ve excluded_services'ı atla (zaten ProductTourServicesList ile render ediliyor)
            if (fieldKey === 'included_services' || fieldKey === 'excluded_services') {
                return;
            }

            // Sadece rich_text field'ları ve has_data = true olanları al
            if (field.field_type !== 'rich_text' || !field.has_data) {
                return;
            }

            // Meta'dan değeri al
            const value = meta[fieldKey];
            if (!value || (typeof value === 'string' && value.trim() === '')) {
                return;
            }

            // Tab group key'i al (yeni yapı) veya tab_name'den oluştur (eski yapı uyumluluğu)
            const tabGroupKey = field.tab_group_key || field.tab_name || 'general';
            
            // Tab name'i al (görüntüleme için)
            const tabName = field.tab_name || field.field_title || 'General';
            
            // Field title'ı dil bazlı al
            let fieldTitle = fieldKey;
            if (field.field_title) {
                fieldTitle = field.field_title;
            }

            // Tab display order'ı al
            const tabDisplayOrder = field.tab_display_order ?? field.sort_order ?? 999;

            // Tab group'a ekle
            if (!tabGroups[tabGroupKey]) {
                tabGroups[tabGroupKey] = [];
            }

            tabGroups[tabGroupKey].push({
                fieldKey,
                fieldTitle,
                content: typeof value === 'string' ? value : String(value),
                tabDisplayOrder,
            });
        });

        // Tab groups'u section ID'ler ile birlikte ekle
        Object.entries(tabGroups).forEach(([tabGroupKey, tabFields]) => {
            // services tab group'unu atla (zaten ProductTourServicesList ile render ediliyor)
            if (tabGroupKey === 'services') {
                return;
            }

            tabFields.sort((a, b) => a.tabDisplayOrder - b.tabDisplayOrder);

            // Diğer açıklamalar: her alan kendi başlığıyla ayrı section
            if (tabGroupKey === OTHER_DESCRIPTIONS_GROUP_KEY) {
                tabFields.forEach((f) => {
                    const fieldDef = fields[f.fieldKey];
                    const sectionId = fieldDef?.tab_section_id
                        || slugifySectionId(f.fieldTitle, f.fieldKey);
                    result.push({
                        tabName: f.fieldTitle,
                        sectionId,
                        showSectionTitle: true,
                        groupKey: OTHER_DESCRIPTIONS_GROUP_KEY,
                        fields: [{
                            fieldKey: f.fieldKey,
                            fieldTitle: f.fieldTitle,
                            content: f.content,
                        }],
                    });
                });
                return;
            }

            const firstField = fields[tabFields[0]?.fieldKey];
            const sectionId = firstField?.tab_section_id ||
                             firstField?.menu_section_id ||
                             tabGroupKey.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
            const tabSectionField = fields['tab_section_' + tabGroupKey];
            const showSectionTitle = tabSectionField?.show_section_title !== false;
            const tabName = firstField?.tab_name ||
                          firstField?.field_title ||
                          tabGroupKey;

            result.push({
                tabName,
                sectionId,
                showSectionTitle,
                fields: tabFields.map(f => ({
                    fieldKey: f.fieldKey,
                    fieldTitle: f.fieldTitle,
                    content: f.content,
                })),
            });
        });

        // Sort order'a göre sırala (tab_display_order veya sort_order kullan)
        return result.sort((a, b) => {
            const aFirstField = a.fields[0];
            const bFirstField = b.fields[0];
            const aField = aFirstField ? fields[aFirstField.fieldKey] : null;
            const bField = bFirstField ? fields[bFirstField.fieldKey] : null;
            const aSort = aField?.tab_display_order ?? aField?.sort_order ?? 999;
            const bSort = bField?.tab_display_order ?? bField?.sort_order ?? 999;
            return aSort - bSort;
        });
    }

    private static isTruthyFlag(value: unknown): boolean {
        if (typeof value === 'boolean') return value;
        if (typeof value === 'number') return value === 1;
        if (typeof value === 'string') {
            return ['1', 'true', 'yes', 'on'].includes(value.trim().toLowerCase());
        }
        return false;
    }
}
